import * as React from 'react';
import AsyncSelect from 'react-select/async';

import {QueryGetAlunos} from '../../../services';
import {OnSelectProps, SelectValueProps, TargetProps} from '../../types';
import {
  UserSelectOption,
  UserSelectOptionThumb,
  UserSelectOptionContent,
  UserSelectOptionTitle,
  UserSelectContainer,
} from './UserSelect.style';

type UserSelectProps = {
  onSelect: OnSelectProps;
  defaultOptions: SelectValueProps[];
  selectValue: SelectValueProps;
  errors?: Record<string, unknown>;
  target?: string;
  usersTargetIds?: (string | number)[];
};

/**
 * UserSelect Component
 * Async select for searching and selecting users
 *
 * Migration Notes:
 * - Previously used WorkoutRoutineContext for errors and workoutRoutine
 * - Now accepts errors, target, and usersTargetIds as props directly
 * - Requirements: 7.3 - Remove legacy context dependencies
 */
export const UserSelect = ({
  onSelect,
  defaultOptions,
  selectValue,
  errors = {},
  target,
  usersTargetIds = [],
}: UserSelectProps): JSX.Element => {
  const [value, setValue] = React.useState(selectValue);
  const [inputSearch, setInputSearch] = React.useState('');
  const [options, setOptions] = React.useState(
    defaultOptions.map((option) => convertDataToUserSelect(option)),
  );
  const onChange = (value) => {
    setValue(value);
    onSelect(value);
  };

  function convertDataToUserSelect(user) {
    return {
      image: user.avatar,
      label: `${user.shortName} - ${user.email}`,
      value: user.id,
    };
  }

  const loadOptions = (inputValue, callback) => {
    setInputSearch(inputValue);
    if (inputValue.length >= 3) {
      QueryGetAlunos(1, inputValue)
        .then(({data}) => {
          const {list, page, totalPages} = data;
          const usersTranslated = list.map((user) =>
            convertDataToUserSelect(user),
          );
          callback(usersTranslated);
        })
        .catch((err) => console.log('error ', err));
    }
  };

  const OptionComponent = ({innerProps, innerRef, data}) => {
    return (
      <UserSelectOption {...innerProps}>
        {!!data.image && (
          <UserSelectOptionThumb>
            <img src={data.image?.uri} width="75" height="75" />
          </UserSelectOptionThumb>
        )}
        <UserSelectOptionContent>
          <UserSelectOptionTitle>{data.label}</UserSelectOptionTitle>
        </UserSelectOptionContent>
      </UserSelectOption>
    );
  };

  const LoadingMessage = (props) => {
    if (inputSearch.length < 3) {
      return (
        <UserSelectOption>
          <UserSelectOptionContent>
            <span>Digite 3 carecteres ou mais para iniciar a busca.</span>
          </UserSelectOptionContent>
        </UserSelectOption>
      );
    } else {
      return (
        <UserSelectOption>
          <UserSelectOptionContent>
            <span>Carregando... </span>
          </UserSelectOptionContent>
        </UserSelectOption>
      );
    }
  };

  const NoOptionsMessage = (props) => (
    <UserSelectOption>
      <UserSelectOptionContent>
        <span>Nenhum usuário encontrado!</span>
      </UserSelectOptionContent>
    </UserSelectOption>
  );

  const hasError =
    !!(errors as any).target &&
    target === TargetProps.users &&
    usersTargetIds.length == 0;

  return (
    <UserSelectContainer error={hasError ? 'error' : ''}>
      <AsyncSelect
        defaultOptions={options}
        placeholder={'Selecione/digite o nome/email do(s) usuário(s)'}
        onChange={onChange}
        components={{
          Option: OptionComponent,
          LoadingMessage: LoadingMessage,
          NoOptionsMessage: NoOptionsMessage,
        }}
        loadOptions={loadOptions}
        isSearchable
        isMulti
        value={value}
      />
    </UserSelectContainer>
  );
};
